import React, { useState, useEffect } from 'react';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import Skeleton from 'ui/Skeleton';
import { Icon } from 'components/ui';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import Tooltip from 'components/shared/Tooltip';
import { LabeledCheckbox } from 'components/shared/checkbox';
import EventNotFoundGraphic from 'components/shared/EventNotFoundGraphic';
import ElementBase from '../base';
import SelectedEvent from './SelectedEvent';
import SelectEvent from './SelectEvent';
import NoEventAccessGraphic from './NoEventAccessGraphic';

const EventElement = ({
  applyChanges,
  handleUpdate,
  content,
  noEventPermission,
  setNoEventPermissions,
}) => {
  const selectedEventId = content.in_person_training_event_id;
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(!!(selectedEventId && !selectedEvent));
  const [eventNotFound, setEventNotFound] = useState(false);

  const handleEventSelection = event => {
    applyChanges({ in_person_training_event_id: event.id });
    handleUpdate({ in_person_training_event_id: event.id });
    setSelectedEvent(event);
  };

  useEffect(() => {
    if (selectedEventId && !selectedEvent && !noEventPermission) {
      request
        .get(routes.TRAINING.EVENT(selectedEventId))
        .then(res => {
          if (res) {
            setSelectedEvent(res);
          }
          setLoading(false);
        })
        .fail(err => {
          if (err.status === 401) {
            setNoEventPermissions(true);
          } else if (err.status === 404) {
            setEventNotFound(true);
          }
        });
    } else if (!selectedEventId && selectedEvent) {
      setSelectedEvent(null);
    }
  }, [
    selectedEventId,
    selectedEvent,
    noEventPermission,
    setNoEventPermissions,
  ]);

  return (
    <div className="EditorElement-content EditorElement--event">
      {noEventPermission && <NoEventAccessGraphic />}
      {eventNotFound && <EventNotFoundGraphic isEditor />}
      {loading && !noEventPermission && !eventNotFound && (
        <>
          <div className="EditorElement-header p-32">
            <Skeleton height={40} className="mb-4" width="100%" />
          </div>
          <div className="EditorElement-body loading p-24">
            <Skeleton height={28} className="mb-4 mt-8" width="25%" />
            <Skeleton height={24} className="mb-8" />
            <Skeleton height={20} className="mb-4" width="75%" />
            <Skeleton height={20} className="mb-4" width="50%" />
            <Skeleton height={20} className="mb-4" width="50%" />
          </div>
        </>
      )}
      {!noEventPermission && !eventNotFound && !loading && (
        <div className="EditorElement-header d-f jc-sb p-32">
          <div className="Element-title d-f fd-c">
            <Text className="mb-4" variant={TEXT_VARIANTS.SUBHEADER}>
              {t('lessons.editor.event.event')}
            </Text>
            <Text variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}>
              {t('lessons.editor.event.event_desc')}
            </Text>
          </div>
          <div className="EditorElement-required d-f">
            <LabeledCheckbox
              className="mr-8"
              label={t('questions.required.label')}
              checked={content.required}
              onChange={e => {
                const change = { required: e.target.checked };
                applyChanges(change);
                handleUpdate(change);
              }}
            />
            <Tooltip text={t('lessons.editor.event.required_tooltip')}>
              <Icon name="info" className="c-gray-light" />
            </Tooltip>
          </div>
        </div>
      )}
      {!noEventPermission && !selectedEvent && !selectedEventId && (
        <SelectEvent setSelectedEvent={handleEventSelection} />
      )}
      {!noEventPermission && selectedEvent && (
        <SelectedEvent
          selectedEvent={selectedEvent}
          selectedEventId={selectedEventId}
        />
      )}
    </div>
  );
};

export default ElementBase(EventElement);
